import * as React from 'react';
import {
    CommandBar,
    DefaultButton,
    Dropdown,
    IDropdownOption,
    ICommandBarItemProps,
    MessageBar,
    MessageBarType,
    Pivot,
    PivotItem,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    AgentTaskListDetailResult,
    AgentTaskProposalVote,
    AgentTaskProposalVoteUpdateResult,
    AgentTaskProposedChangeResult,
    AgentTaskProposedChangeResultSet,
    AgentTaskProposedChangesSortKey,
    AgentTaskProposedChangesView,
    AgentTaskSnapshotSummary,
    AgentTaskSortDirection,
    PAGE_SIZE_OPTIONS,
    PageShell,
    buildAdoPullRequestUrl,
    formatDate,
    formatNumber,
    getJobStatusLabel,
    getPullRequestStatusWithHealthLabel,
    parseOptionalInt,
    updateSearchParams,
    useCurrentService,
    useDocumentTitle,
} from '../shared';
import { ApiHelper } from '../../../ApiHelper';
import { EnqueueTasksPanel } from '../panels/EnqueueTasksPanel';
import { TaskDetailPanel } from '../panels/TaskDetailPanel';
import {
    getAgentTaskProposedChanges,
    updateAgentTaskProposedChangeVote,
} from '../proposedChangesApi';
import { StructuredProposedChanges } from '../proposedChanges/AgentTaskProposedChangeComponents';
import { getOkAllProposalVote } from '../proposedChanges/TaskScopedProposalReview';
import { AgentTaskProposalVote as ProposalVote } from '../types';

interface SelectedTask {
    taskListId: number;
    taskId: number;
    snapshotId: number;
}

interface ProposedChangeScopeGroup {
    key: string;
    taskListId: number;
    taskId: number;
    taskScope: string;
    resultSets: AgentTaskProposedChangeResultSet[];
}

interface ScopePullRequest {
    prId: number;
    pullRequestStatus: number;
    pullRequestHealth?: number | null;
}

const ACTIVE_JOB_STATUSES = new Set([0, 1, 2, 5, 6]);

function calculateProposalCounts(resultSets: AgentTaskProposedChangeResultSet[]) {
    const currentProposals = new Map<number, AgentTaskProposedChangeResult>();
    resultSets.forEach(resultSet => resultSet.proposedChangeEvents.forEach(change => {
        if (change.isCurrent) {
            currentProposals.set(change.analysisItemId, change);
        }
    }));
    const active = Array.from(currentProposals.values()).filter(change => change.outcome !== 'discarded');
    return {
        activeProposalCount: active.length,
        votedProposalCount: active.filter(change => change.vote !== null).length,
        okProposalCount: active.filter(change => change.vote === ProposalVote.Ok).length,
    };
}

function applyRefreshedProposals(
    resultSets: AgentTaskProposedChangeResultSet[],
    proposals: AgentTaskProposedChangeResult[],
) {
    const proposalsByOrigin = new Map<number, AgentTaskProposedChangeResult[]>();
    proposals.forEach(proposal => {
        const values = proposalsByOrigin.get(proposal.originWorkItemId) ?? [];
        values.push(proposal);
        proposalsByOrigin.set(proposal.originWorkItemId, values);
    });

    return resultSets.map(resultSet => {
        const refreshed = proposalsByOrigin.get(resultSet.workItemId);
        if (refreshed) {
            return {
                ...resultSet,
                proposedChangeEvents: [
                    ...resultSet.proposedChangeEvents.filter(change => !change.isCurrent),
                    ...refreshed,
                ],
            };
        }

        const proposalsById = new Map(proposals.map(proposal => [proposal.analysisItemId, proposal]));
        return {
            ...resultSet,
            proposedChangeEvents: resultSet.proposedChangeEvents.map(change =>
                proposalsById.get(change.analysisItemId) ?? change),
        };
    });
}

function groupResultSetsByScope(resultSets: AgentTaskProposedChangeResultSet[]) {
    const groups = new Map<string, ProposedChangeScopeGroup>();
    resultSets.forEach(resultSet => {
        const key = `${resultSet.taskListId}:${resultSet.taskId}`;
        const group = groups.get(key);
        if (group) {
            group.resultSets.push(resultSet);
        } else {
            groups.set(key, {
                key,
                taskListId: resultSet.taskListId,
                taskId: resultSet.taskId,
                taskScope: resultSet.taskScope,
                resultSets: [resultSet],
            });
        }
    });

    return Array.from(groups.values());
}

function getScopePullRequests(group: ProposedChangeScopeGroup) {
    const pullRequests = new Map<number, ScopePullRequest>();
    [...group.resultSets]
        .sort((left, right) => right.workItemId - left.workItemId)
        .forEach(resultSet => {
            resultSet.pullRequests.forEach(pullRequest => {
                if (!pullRequests.has(pullRequest.prId)) {
                    pullRequests.set(pullRequest.prId, pullRequest);
                }
            });
            if (resultSet.prId !== undefined && resultSet.prId !== null && !pullRequests.has(resultSet.prId)) {
                pullRequests.set(resultSet.prId, {
                    prId: resultSet.prId,
                    pullRequestStatus: resultSet.pullRequestStatus,
                    pullRequestHealth: resultSet.pullRequestHealth,
                });
            }
        });
    return Array.from(pullRequests.values());
}

function getScopeLastAnalyzedDate(group: ProposedChangeScopeGroup) {
    return [...group.resultSets]
        .map(resultSet => resultSet.agentEndDate ?? resultSet.agentStartDate)
        .filter((value): value is string => !!value)
        .sort((left, right) => Date.parse(right) - Date.parse(left))[0];
}

function getScopeGainEstimatePercent(group: ProposedChangeScopeGroup) {
    const values = group.resultSets
        .map(resultSet => resultSet.expectedSnapshotReductionPercent)
        .filter((value): value is number => value !== undefined && value !== null && Number.isFinite(value));
    return values.length > 0 ? Math.max(...values) : undefined;
}

function CandidateTimeline({ resultSets }: { resultSets: AgentTaskProposedChangeResultSet[] }) {
    const timelines = new Map<string, {
        candidateId: string;
        events: Array<{ workItemId: number; attemptNumber?: number | null; outcome: string; summary: string }>;
    }>();

    resultSets.forEach(resultSet => {
        resultSet.proposedChangeEvents.forEach(change => {
            const key = `${resultSet.taskListId}:${resultSet.taskId}:${resultSet.jobName}:${change.candidateId}`;
            const timeline = timelines.get(key) ?? { candidateId: change.candidateId, events: [] };
            timeline.events.push({
                workItemId: resultSet.workItemId,
                attemptNumber: resultSet.attemptNumber,
                outcome: change.outcome,
                summary: change.summary || change.text,
            });
            timelines.set(key, timeline);
        });
    });

    const repeated = Array.from(timelines.values()).filter(timeline => timeline.events.length > 1);
    if (repeated.length === 0) return null;

    return (
        <details>
            <summary style={{ cursor: 'pointer' }}>Candidate timelines on this page</summary>
            <Stack tokens={{ childrenGap: 8 }} styles={{ root: { marginTop: 8 } }}>
                {repeated.map(timeline => (
                    <div key={timeline.candidateId} style={{ borderLeft: '3px solid #0078d4', paddingLeft: 10 }}>
                        <strong>{timeline.candidateId}</strong>
                        <div>
                            {timeline.events
                                .sort((left, right) => (left.attemptNumber ?? 0) - (right.attemptNumber ?? 0))
                                .map(event => `Attempt ${event.attemptNumber ?? '?'}: ${event.outcome} — ${event.summary}`)
                                .join(' → ')}
                        </div>
                    </div>
                ))}
            </Stack>
        </details>
    );
}

export function AgentTaskProposedChangesPage() {
    const navigate = useNavigate();
    const routeParams = useParams();
    const serviceName = routeParams.serviceName;
    const routeTaskListId = parseOptionalInt(routeParams.taskListId);
    const routeTaskId = parseOptionalInt(routeParams.taskId);
    const isScopeDetail = routeTaskListId !== undefined && routeTaskId !== undefined;
    useDocumentTitle(isScopeDetail ? 'Proposed change details' : 'Proposed changes');
    const service = useCurrentService(serviceName);
    const [searchParams, setSearchParams] = useSearchParams();
    const view: AgentTaskProposedChangesView = searchParams.get('view') === 'history' ? 'history' : 'current';
    const pageNumber = Math.max(0, parseOptionalInt(searchParams.get('pageNumber')) ?? 0);
    const pageSize = Math.max(1, parseOptionalInt(searchParams.get('pageSize')) ?? 25);
    const sortByParam = searchParams.get('sortBy');
    const sortBy: AgentTaskProposedChangesSortKey =
        sortByParam === 'scope' || sortByParam === 'gainEstimate' || sortByParam === 'lastAnalyzed'
            ? sortByParam
            : 'lastAnalyzed';
    const sortDirection: AgentTaskSortDirection = searchParams.get('sortDirection') === 'asc' ? 'asc' : 'desc';
    const taskListId = routeTaskListId ?? parseOptionalInt(searchParams.get('taskListId'));
    const taskId = routeTaskId ?? parseOptionalInt(searchParams.get('taskId'));
    const workItemId = parseOptionalInt(searchParams.get('workItemId'));
    const candidateId = searchParams.get('candidateId') ?? '';
    const jobName = searchParams.get('jobName') ?? '';
    const [candidateFilter, setCandidateFilter] = React.useState(candidateId);
    const [jobFilter, setJobFilter] = React.useState(jobName);
    const [resultSets, setResultSets] = React.useState<AgentTaskProposedChangeResultSet[]>([]);
    const [totalCount, setTotalCount] = React.useState(0);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [selectedTask, setSelectedTask] = React.useState<SelectedTask | null>(null);
    const [isEnqueuePanelOpen, setEnqueuePanelOpen] = React.useState(false);
    const [enqueueLoading, setEnqueueLoading] = React.useState(false);
    const [enqueueError, setEnqueueError] = React.useState<string | null>(null);
    const [enqueueTaskList, setEnqueueTaskList] = React.useState<AgentTaskListDetailResult | null>(null);
    const [enqueueSnapshot, setEnqueueSnapshot] = React.useState<AgentTaskSnapshotSummary | null>(null);
    const [proposalCounts, setProposalCounts] = React.useState({
        activeProposalCount: 0,
        votedProposalCount: 0,
        okProposalCount: 0,
    });
    const [selectedProposalIds, setSelectedProposalIds] = React.useState<Set<number>>(new Set());
    const [updatingVoteIds, setUpdatingVoteIds] = React.useState<Set<number>>(new Set());
    const [voteError, setVoteError] = React.useState<string | null>(null);
    const [okAllLoading, setOkAllLoading] = React.useState(false);
    const resultSetsRef = React.useRef<AgentTaskProposedChangeResultSet[]>([]);

    function replaceResultSets(next: AgentTaskProposedChangeResultSet[]) {
        resultSetsRef.current = next;
        setResultSets(next);
    }

    function pruneSelectedProposals(resultSetsToCheck: AgentTaskProposedChangeResultSet[]) {
        const eligibleIds = new Set(
            resultSetsToCheck.flatMap(resultSet => resultSet.proposedChangeEvents)
                .filter(change =>
                    change.isCurrent
                    && change.outcome !== 'discarded'
                    && change.vote === ProposalVote.Ok)
                .map(change => change.analysisItemId),
        );
        setSelectedProposalIds(current => new Set(
            Array.from(current).filter(analysisItemId => eligibleIds.has(analysisItemId)),
        ));
    }

    function applyProposalUpdate(result: AgentTaskProposalVoteUpdateResult) {
        const next = applyRefreshedProposals(resultSetsRef.current, result.proposals);
        replaceResultSets(next);
        setProposalCounts({
            activeProposalCount: result.activeProposalCount,
            votedProposalCount: result.votedProposalCount,
            okProposalCount: result.okProposalCount,
        });
        pruneSelectedProposals(next);
    }

    const loadChanges = React.useCallback(async () => {
        if (!serviceName) return;
        setLoading(true);
        setError(null);
        try {
            const result = await getAgentTaskProposedChanges(serviceName, {
                view,
                pageNumber,
                pageSize,
                sortBy,
                sortDirection,
                taskListId,
                taskId,
                workItemId,
                candidateId: candidateId || undefined,
                jobName: jobName || undefined,
            });
            replaceResultSets(result.resultSets);
            setTotalCount(result.totalCount);
            setProposalCounts(calculateProposalCounts(result.resultSets));
            setSelectedProposalIds(new Set());
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load proposed changes.');
            replaceResultSets([]);
            setTotalCount(0);
            setProposalCounts(calculateProposalCounts([]));
            setSelectedProposalIds(new Set());
        } finally {
            setLoading(false);
        }
    }, [candidateId, jobName, pageNumber, pageSize, serviceName, sortBy, sortDirection, taskId, taskListId, view, workItemId]);

    React.useEffect(() => {
        void loadChanges();
    }, [loadChanges]);

    React.useEffect(() => {
        setCandidateFilter(candidateId);
        setJobFilter(jobName);
    }, [candidateId, jobName]);

    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    const scopeGroups = React.useMemo(() => groupResultSetsByScope(resultSets), [resultSets]);
    const detailGroup = isScopeDetail ? scopeGroups[0] : undefined;
    const filterDescription = [
        taskListId !== undefined ? `task list #${taskListId}` : null,
        taskId !== undefined ? `task #${taskId}` : null,
        workItemId !== undefined ? `work item #${workItemId}` : null,
        candidateId ? `candidate ${candidateId}` : null,
        jobName ? `job ${jobName}` : null,
    ].filter(Boolean).join(', ');

    const commandItems: ICommandBarItemProps[] = [
        {
            key: 'back',
            text: isScopeDetail ? 'Back to proposed changes' : 'Back to task lists',
            iconProps: { iconName: 'Back' },
            onClick: () => navigate(isScopeDetail
                ? `/service/${serviceName}/agenttasks/changes`
                : `/service/${serviceName}/agenttasks`),
        },
        {
            key: 'refresh',
            text: 'Refresh',
            iconProps: { iconName: 'Refresh' },
            onClick: () => void loadChanges(),
        },
        {
            key: 'questions',
            text: 'Clarification questions',
            iconProps: { iconName: 'Help' },
            onClick: () => navigate(`/service/${serviceName}/agenttasks/questions${taskListId !== undefined ? `?taskListId=${taskListId}${taskId !== undefined ? `&taskId=${taskId}` : ''}` : ''}`),
        },
    ];

    function navigateToScope(group: ProposedChangeScopeGroup) {
        navigate(
            `/service/${serviceName}/agenttasks/changes/${group.taskListId}/${group.taskId}`
            + (view === 'history' ? '?view=history' : ''),
        );
    }

    function applyTextFilters() {
        updateSearchParams(searchParams, setSearchParams, {
            candidateId: candidateFilter.trim() || null,
            jobName: jobFilter.trim() || null,
            pageNumber: 0,
        });
    }

    async function handleVoteChange(
        change: AgentTaskProposedChangeResult,
        vote: AgentTaskProposalVote | null,
    ) {
        if (!serviceName || !change.isCurrent || updatingVoteIds.size > 0 || okAllLoading) {
            return;
        }

        setVoteError(null);
        setUpdatingVoteIds(current => new Set(current).add(change.analysisItemId));
        const previousChange = change;
        const optimistic = resultSetsRef.current.map(resultSet => ({
            ...resultSet,
            proposedChangeEvents: resultSet.proposedChangeEvents.map(item =>
                item.analysisItemId === change.analysisItemId ? { ...item, vote } : item),
        }));
        replaceResultSets(optimistic);
        setProposalCounts(calculateProposalCounts(optimistic));
        pruneSelectedProposals(optimistic);

        try {
            const result = await updateAgentTaskProposedChangeVote(
                serviceName,
                change.analysisItemId,
                vote,
                change.rowVersion,
            );
            applyProposalUpdate(result);
        } catch (err) {
            const rolledBack = resultSetsRef.current.map(resultSet => ({
                ...resultSet,
                proposedChangeEvents: resultSet.proposedChangeEvents.map(item =>
                    item.analysisItemId === previousChange.analysisItemId ? previousChange : item),
            }));
            replaceResultSets(rolledBack);
            setProposalCounts(calculateProposalCounts(rolledBack));
            pruneSelectedProposals(rolledBack);
            setVoteError(err instanceof Error ? err.message : 'Failed to update the proposal vote.');
        } finally {
            setUpdatingVoteIds(current => {
                const next = new Set(current);
                next.delete(change.analysisItemId);
                return next;
            });
        }
    }

    async function handleOkAll() {
        if (!serviceName || !detailGroup || okAllLoading) {
            return;
        }

        const unvotedById = new Map<number, {
            change: AgentTaskProposedChangeResult;
            taskSnapshotInclusivePercent?: number | null;
        }>();
        detailGroup.resultSets.forEach(resultSet => {
            resultSet.proposedChangeEvents.forEach(change => {
                if (change.isCurrent && change.outcome !== 'discarded' && change.vote === null) {
                    unvotedById.set(change.analysisItemId, {
                        change,
                        taskSnapshotInclusivePercent: resultSet.taskSnapshotInclusivePercent,
                    });
                }
            });
        });
        const unvotedProposals = Array.from(unvotedById.values());
        if (unvotedProposals.length === 0) {
            return;
        }

        setVoteError(null);
        setOkAllLoading(true);
        setUpdatingVoteIds(new Set(
            unvotedProposals.map(({ change }) => change.analysisItemId),
        ));

        try {
            for (const target of unvotedProposals) {
                const currentChange = resultSetsRef.current
                    .flatMap(resultSet => resultSet.proposedChangeEvents)
                    .find(change => change.analysisItemId === target.change.analysisItemId)
                    ?? target.change;
                const result = await updateAgentTaskProposedChangeVote(
                    serviceName,
                    currentChange.analysisItemId,
                    getOkAllProposalVote(
                        currentChange,
                        target.taskSnapshotInclusivePercent,
                    ),
                    currentChange.rowVersion,
                );
                applyProposalUpdate(result);
            }
        } catch (err) {
            setVoteError(err instanceof Error ? err.message : 'Failed to vote all unvoted proposed changes.');
        } finally {
            setOkAllLoading(false);
            setUpdatingVoteIds(new Set());
        }
    }

    function handleProposalSelection(change: AgentTaskProposedChangeResult, selected: boolean) {
        setSelectedProposalIds(current => {
            const next = new Set(current);
            if (selected) {
                next.add(change.analysisItemId);
            } else {
                next.delete(change.analysisItemId);
            }
            return next;
        });
    }

    function updateSort(nextSortBy: AgentTaskProposedChangesSortKey) {
        const nextDirection: AgentTaskSortDirection =
            sortBy === nextSortBy
                ? (sortDirection === 'asc' ? 'desc' : 'asc')
                : (nextSortBy === 'scope' ? 'asc' : 'desc');
        updateSearchParams(searchParams, setSearchParams, {
            sortBy: nextSortBy,
            sortDirection: nextDirection,
            pageNumber: 0,
        });
    }

    function renderSortableHeader(label: string, key: AgentTaskProposedChangesSortKey, minWidth?: number) {
        const isSorted = sortBy === key;
        return (
            <th
                style={{ padding: 10, minWidth }}
                aria-sort={isSorted ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
            >
                <button
                    type="button"
                    onClick={() => updateSort(key)}
                    style={{
                        background: 'none',
                        border: 0,
                        cursor: 'pointer',
                        font: 'inherit',
                        fontWeight: 600,
                        padding: 0,
                    }}
                >
                    {label}{isSorted ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : ''}
                </button>
            </th>
        );
    }

    async function openEnqueuePanel(group: ProposedChangeScopeGroup) {
        if (!serviceName || group.resultSets.length === 0) {
            return;
        }

        setEnqueueLoading(true);
        setEnqueueError(null);
        try {
            const taskList = await ApiHelper(
                `/api/service/${serviceName}/agenttasklists/${group.taskListId}`,
            ) as AgentTaskListDetailResult;
            const proposalSnapshotId = [...group.resultSets]
                .sort((left, right) => right.workItemId - left.workItemId)[0]
                .snapshotId;
            const snapshot = taskList.snapshots.find(item => item.snapshotId === proposalSnapshotId)
                ?? (taskList.latestSnapshot?.snapshotId === proposalSnapshotId ? taskList.latestSnapshot : null);
            if (!snapshot) {
                throw new Error(`Snapshot #${proposalSnapshotId} is no longer available for enqueue.`);
            }

            setEnqueueTaskList(taskList);
            setEnqueueSnapshot(snapshot);
            setEnqueuePanelOpen(true);
        } catch (err) {
            setEnqueueTaskList(null);
            setEnqueueSnapshot(null);
            setEnqueueError(err instanceof Error ? err.message : 'Failed to prepare the enqueue action.');
        } finally {
            setEnqueueLoading(false);
        }
    }

    // Current proposals remain visible during a rerun, so block enqueue until that newer attempt settles.
    const hasActiveNewerAttempt = detailGroup?.resultSets.some(resultSet =>
        resultSet.newerAttemptWorkItemId !== undefined
        && resultSet.newerAttemptWorkItemId !== null
        && resultSet.newerAttemptStatus !== undefined
        && ACTIVE_JOB_STATUSES.has(resultSet.newerAttemptStatus));
    const hasUnvotedActiveProposals = proposalCounts.votedProposalCount < proposalCounts.activeProposalCount;
    const hasApplicableOkProposals = proposalCounts.okProposalCount > 0;
    const currentUnvotedCount = resultSets.flatMap(resultSet => resultSet.proposedChangeEvents)
        .filter(change => change.isCurrent && change.outcome !== 'discarded' && change.vote === null)
        .length;

    return (
        <PageShell title={isScopeDetail ? (detailGroup?.taskScope ?? 'Proposed change details') : 'Proposed changes'}>
            <CommandBar items={commandItems} />
            <Stack horizontal horizontalAlign="space-between" verticalAlign="end" wrap tokens={{ childrenGap: 12 }}>
                <div>
                    <Pivot
                        selectedKey={view}
                        onLinkClick={item => updateSearchParams(searchParams, setSearchParams, {
                            view: item?.props.itemKey === 'history' ? 'history' : null,
                            pageNumber: 0,
                        })}
                    >
                        <PivotItem headerText="Current" itemKey="current" />
                        <PivotItem headerText="History" itemKey="history" />
                    </Pivot>
                    {!isScopeDetail && filterDescription && <Text>Filtered to {filterDescription}.</Text>}
                </div>
                {!isScopeDetail && (
                    <Stack horizontal verticalAlign="end" wrap tokens={{ childrenGap: 8 }}>
                        <TextField
                            label="Candidate id"
                            value={candidateFilter}
                            onChange={(_, value) => setCandidateFilter(value ?? '')}
                        />
                        <TextField
                            label="Job name"
                            value={jobFilter}
                            onChange={(_, value) => setJobFilter(value ?? '')}
                        />
                        <DefaultButton text="Apply filters" onClick={applyTextFilters} />
                        <div style={{ minWidth: 120 }}>
                            <Dropdown
                                label="Page size"
                                options={PAGE_SIZE_OPTIONS as IDropdownOption[]}
                                selectedKey={pageSize}
                                onChange={(_, option) => updateSearchParams(searchParams, setSearchParams, {
                                    pageSize: Number(option?.key),
                                    pageNumber: 0,
                                })}
                            />
                        </div>
                    </Stack>
                )}
            </Stack>

            {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
            {enqueueError && <MessageBar messageBarType={MessageBarType.error}>{enqueueError}</MessageBar>}
            {voteError && <MessageBar messageBarType={MessageBarType.error}>{voteError}</MessageBar>}

            {isScopeDetail && view === 'history' && <CandidateTimeline resultSets={resultSets} />}

            {loading ? (
                <Spinner size={SpinnerSize.large} label="Loading proposed changes..." />
            ) : resultSets.length === 0 ? (
                <MessageBar messageBarType={MessageBarType.info}>
                    {view === 'current'
                        ? 'No current structured proposed changes were found.'
                        : 'No proposed-change history was found.'}
                </MessageBar>
            ) : isScopeDetail && detailGroup ? (
                <Stack tokens={{ childrenGap: 16 }}>
                    <Stack horizontal horizontalAlign="space-between" verticalAlign="center" wrap tokens={{ childrenGap: 8 }}>
                        <Text>
                            Task #{detailGroup.taskId} • Task list #{detailGroup.taskListId}
                        </Text>
                        <Stack horizontal wrap tokens={{ childrenGap: 8 }}>
                            {view === 'current' && (
                                <>
                                    <DefaultButton
                                        text={okAllLoading ? 'Marking Ok...' : 'Ok all proposed changes'}
                                        disabled={okAllLoading || updatingVoteIds.size > 0 || currentUnvotedCount === 0}
                                        onClick={() => void handleOkAll()}
                                    />
                                    <DefaultButton
                                        text={enqueueLoading ? 'Preparing enqueue...' : 'Enqueue job'}
                                        disabled={enqueueLoading
                                            || hasActiveNewerAttempt
                                            || !hasApplicableOkProposals
                                            || hasUnvotedActiveProposals}
                                        title={hasActiveNewerAttempt
                                            ? 'A newer analysis attempt is still active. Wait for it to finish before enqueueing from these proposals.'
                                            : !hasApplicableOkProposals
                                                ? 'At least one current active proposal must be voted Ok before enqueueing.'
                                                : hasUnvotedActiveProposals
                                                    ? 'Every current active proposal must be voted before enqueueing.'
                                                    : undefined}
                                        onClick={() => void openEnqueuePanel(detailGroup)}
                                    />
                                </>
                            )}
                            <DefaultButton
                                text="View task details"
                                onClick={() => setSelectedTask({
                                    taskListId: detailGroup.taskListId,
                                    taskId: detailGroup.taskId,
                                    snapshotId: detailGroup.resultSets[0].snapshotId,
                                })}
                            />
                        </Stack>
                    </Stack>
                    {view === 'current' && hasActiveNewerAttempt && (
                        <MessageBar messageBarType={MessageBarType.warning}>
                            Enqueue is unavailable while a newer analysis attempt is active, because the current proposals may be replaced.
                        </MessageBar>
                    )}
                    {view === 'current' && !hasApplicableOkProposals && (
                        <MessageBar messageBarType={MessageBarType.info}>
                            No current active proposals are voted Ok. Vote at least one proposal Ok before enqueueing.
                        </MessageBar>
                    )}
                    {view === 'current' && hasApplicableOkProposals && hasUnvotedActiveProposals && (
                        <MessageBar messageBarType={MessageBarType.info}>
                            Some active proposals are still unvoted. Review them or use Ok all proposed changes before enqueueing.
                        </MessageBar>
                    )}
                    {view === 'current' && selectedProposalIds.size > 0 && (
                        <MessageBar messageBarType={MessageBarType.info}>
                            {selectedProposalIds.size} proposal{selectedProposalIds.size === 1 ? '' : 's'} selected.
                            Enqueue will submit only the checked Ok proposal{selectedProposalIds.size === 1 ? '' : 's'}.
                        </MessageBar>
                    )}
                    {detailGroup.resultSets.map(resultSet => {
                        const prUrl = buildAdoPullRequestUrl(service, resultSet.prId);
                        return (
                            <section
                                key={resultSet.workItemId}
                                style={{ border: '1px solid #d2d0ce', borderRadius: 6, padding: 16 }}
                            >
                                <Text variant="mediumPlus">
                                    {resultSet.jobName} • Attempt {resultSet.attemptNumber ?? '?'}
                                </Text>
                                <div>
                                    <Text variant="small">
                                        Snapshot #{resultSet.snapshotId} • Work item #{resultSet.workItemId}
                                        {' • '}{getJobStatusLabel(resultSet.jobStatus)}
                                        {' • '}Completed {formatDate(resultSet.agentEndDate ?? resultSet.agentStartDate)}
                                        {resultSet.prId !== undefined && resultSet.prId !== null && (
                                            <>
                                                {' • '}
                                                {prUrl ? (
                                                    <a href={prUrl} target="_blank" rel="noreferrer">
                                                        PR #{resultSet.prId}
                                                    </a>
                                                ) : `PR #${resultSet.prId}`}
                                                {' • '}{getPullRequestStatusWithHealthLabel(
                                                    resultSet.pullRequestStatus,
                                                    resultSet.pullRequestHealth,
                                                    true,
                                                )}
                                            </>
                                        )}
                                    </Text>
                                </div>
                                <Stack tokens={{ childrenGap: 12 }} styles={{ root: { marginTop: 12 } }}>
                                    {resultSet.newerAttemptWorkItemId && (
                                        <MessageBar messageBarType={MessageBarType.warning}>
                                            A newer attempt (work item #{resultSet.newerAttemptWorkItemId}
                                            {resultSet.newerAttemptStatus !== undefined
                                                ? `, ${getJobStatusLabel(resultSet.newerAttemptStatus)}`
                                                : ''}) has not replaced this latest successful result.
                                        </MessageBar>
                                    )}
                                    <StructuredProposedChanges
                                        resultKey={resultSet.workItemId}
                                        resultSummary={resultSet.resultSummary}
                                        assumptions={resultSet.assumptions}
                                        noChangeReason={resultSet.noChangeReason}
                                        expectedReductionPercent={resultSet.expectedReductionPercent}
                                        taskSnapshotInclusivePercent={resultSet.taskSnapshotInclusivePercent}
                                        expectedSnapshotReductionPercent={resultSet.expectedSnapshotReductionPercent}
                                        proposedChangeEvents={resultSet.proposedChangeEvents}
                                        questions={resultSet.questions}
                                        onVoteChange={view === 'current' ? handleVoteChange : undefined}
                                        updatingVoteIds={updatingVoteIds.size > 0
                                            ? new Set(resultSet.proposedChangeEvents.map(change => change.analysisItemId))
                                            : updatingVoteIds}
                                        selectionEnabled={view === 'current'}
                                        selectedProposalIds={selectedProposalIds}
                                        onSelectionChange={handleProposalSelection}
                                    />
                                </Stack>
                            </section>
                        );
                    })}
                </Stack>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ textAlign: 'left', borderBottom: '2px solid #8a8886' }}>
                                <th style={{ padding: 10, width: 60 }}>#</th>
                                {renderSortableHeader('Scope', 'scope')}
                                {renderSortableHeader('Gain estimate', 'gainEstimate', 130)}
                                <th style={{ padding: 10, minWidth: 220 }}>PRs and status</th>
                                {renderSortableHeader('Last analyzed', 'lastAnalyzed', 160)}
                            </tr>
                        </thead>
                        <tbody>
                            {scopeGroups.map((group, index) => {
                                const pullRequests = getScopePullRequests(group);
                                const lastAnalyzedDate = getScopeLastAnalyzedDate(group);
                                const gainEstimatePercent = getScopeGainEstimatePercent(group);
                                return (
                                    <tr
                                        key={group.key}
                                        tabIndex={0}
                                        aria-label={`View proposed changes for ${group.taskScope}`}
                                        onClick={() => navigateToScope(group)}
                                        onKeyDown={event => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                navigateToScope(group);
                                            }
                                        }}
                                        style={{ borderBottom: '1px solid #edebe9', cursor: 'pointer' }}
                                    >
                                        <td style={{ padding: 10 }}>{pageNumber * pageSize + index + 1}</td>
                                        <td style={{ padding: 10 }}>
                                            <strong>{group.taskScope}</strong>
                                            <div>
                                                <Text variant="small">
                                                    Task #{group.taskId} • Task list #{group.taskListId}
                                                </Text>
                                            </div>
                                        </td>
                                        <td style={{ padding: 10 }}>
                                            {gainEstimatePercent === undefined
                                                ? '-'
                                                : `${formatNumber(gainEstimatePercent)}%`}
                                        </td>
                                        <td style={{ padding: 10 }}>
                                            {pullRequests.length === 0 ? (
                                                <Text>No PR</Text>
                                            ) : (
                                                <Stack tokens={{ childrenGap: 4 }}>
                                                    {pullRequests.map(pullRequest => {
                                                        const prUrl = buildAdoPullRequestUrl(service, pullRequest.prId);
                                                        return (
                                                            <div key={pullRequest.prId}>
                                                                {prUrl ? (
                                                                    <a
                                                                        href={prUrl}
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        onClick={event => event.stopPropagation()}
                                                                    >
                                                                        PR #{pullRequest.prId}
                                                                    </a>
                                                                ) : `PR #${pullRequest.prId}`}
                                                                {' • '}{getPullRequestStatusWithHealthLabel(
                                                                    pullRequest.pullRequestStatus,
                                                                    pullRequest.pullRequestHealth,
                                                                    true,
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </Stack>
                                            )}
                                        </td>
                                        <td style={{ padding: 10 }}>
                                            {lastAnalyzedDate ? formatDate(lastAnalyzedDate) : '—'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {!isScopeDetail && <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <DefaultButton
                        text="Previous"
                        disabled={pageNumber <= 0}
                        onClick={() => updateSearchParams(searchParams, setSearchParams, { pageNumber: pageNumber - 1 })}
                    />
                    <DefaultButton
                        text="Next"
                        disabled={pageNumber + 1 >= totalPages}
                        onClick={() => updateSearchParams(searchParams, setSearchParams, { pageNumber: pageNumber + 1 })}
                    />
                    <Text>Page {pageNumber + 1} of {totalPages} • {totalCount} scope(s) with proposals</Text>
                </Stack>
            </Stack>}

            {serviceName && selectedTask && (
                <TaskDetailPanel
                    isOpen={true}
                    serviceName={serviceName}
                    taskListId={selectedTask.taskListId}
                    taskId={selectedTask.taskId}
                    snapshotId={selectedTask.snapshotId}
                    onDismiss={() => setSelectedTask(null)}
                />
            )}
            {serviceName && detailGroup && (
                <EnqueueTasksPanel
                    isOpen={isEnqueuePanelOpen}
                    serviceName={serviceName}
                    taskList={enqueueTaskList}
                    snapshot={enqueueSnapshot}
                    taskIds={[detailGroup.taskId]}
                    selectedProposalIds={Array.from(selectedProposalIds)}
                    onDismiss={() => setEnqueuePanelOpen(false)}
                    onEnqueued={() => void loadChanges()}
                />
            )}
        </PageShell>
    );
}
